import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MessageSquare, 
  GraduationCap, 
  BookOpen, 
  TrendingUp,
  Sparkles,
  ArrowRight,
  MapPin,
  Target,
  Brain
} from 'lucide-react';
import type { StudentInfo } from '@/api/chat';

interface WelcomeScreenProps {
  onStartChat: (question: string) => void;
  educationLevel?: 'senior' | 'junior';
  studentInfo?: StudentInfo;
}

interface QuestionItem {
  icon: React.ReactNode;
  question: string;
  category: string;
}

/** 根据已有学生信息动态生成引导问题 */
function generateQuestions(studentInfo: StudentInfo, isJunior: boolean): QuestionItem[] {
  const questions: QuestionItem[] = [];
  const hasScore = !!studentInfo.totalScore;
  const hasTargetLevel = !!studentInfo.targetUniversityLevel;
  const hasTargetCities = studentInfo.targetCities && studentInfo.targetCities.length > 0;
  const hasPreferredMajors = studentInfo.preferredMajors && studentInfo.preferredMajors.length > 0;
  const hasStream = !!studentInfo.stream;

  // 1. 如果有成绩，优先显示个性化推荐问题
  if (hasScore) {
    const score = studentInfo.totalScore!;
    if (hasTargetLevel) {
      const levelText = isJunior
        ? (studentInfo.targetUniversityLevel === '985' ? '重点高中' : studentInfo.targetUniversityLevel === '211' ? '一级达标高中' : studentInfo.targetUniversityLevel === '双一流' ? '二级达标高中' : '普通高中')
        : (studentInfo.targetUniversityLevel === '985' ? '985' : studentInfo.targetUniversityLevel === '211' ? '211' : studentInfo.targetUniversityLevel === '双一流' ? '双一流' : '普通本科');
      questions.push({
        icon: <Target className="w-5 h-5" />,
        question: `我的${score}分能上${levelText}院校吗？`,
        category: '个性化评估'
      });
    } else {
      questions.push({
        icon: <GraduationCap className="w-5 h-5" />,
        question: `我的${score}分可以报什么学校？`,
        category: isJunior ? '升学推荐' : '志愿推荐'
      });
    }
  } else {
    // 没有成绩时，引导输入成绩
    questions.push({
      icon: <GraduationCap className="w-5 h-5" />,
      question: isJunior ? "我的成绩可以报什么高中？" : "我的成绩可以报什么学校？",
      category: isJunior ? '升学推荐' : '志愿推荐'
    });
  }

  // 2. 如果没有目标城市，问城市选择
  if (!hasTargetCities) {
    questions.push({
      icon: <MapPin className="w-5 h-5" />,
      question: isJunior ? "厦门学生去福州读高中好吗？" : "哪些城市适合我上大学发展？",
      category: '城市选择'
    });
  }

  // 3. 如果没有偏好专业，问专业选择
  if (!hasPreferredMajors) {
    questions.push({
      icon: <Brain className="w-5 h-5" />,
      question: isJunior ? "高中选科怎么选对未来专业有利？" : "适合我的专业有哪些？",
      category: '专业指导'
    });
  }

  // 4. 政策解读（始终保留）
  questions.push({
    icon: <BookOpen className="w-5 h-5" />,
    question: isJunior ? "中考志愿填报要注意什么？" : "什么是平行志愿？如何填报？",
    category: '政策解读'
  });

  // 5. 如果已有大部分信息，提供更深入的问题
  if (hasScore && hasTargetLevel && hasTargetCities && hasPreferredMajors) {
    questions.push({
      icon: <TrendingUp className="w-5 h-5" />,
      question: isJunior 
        ? "如何制定冲刺重点高中的学习计划？" 
        : "帮我分析冲稳保志愿方案",
      category: '深度分析'
    });
  } else if (!hasScore) {
    questions.push({
      icon: <TrendingUp className="w-5 h-5" />,
      question: isJunior ? "如何选择适合的高中？" : "如何选择适合自己的专业？",
      category: '升学指导'
    });
  }

  return questions;
}

export default function WelcomeScreen({ onStartChat, educationLevel = 'senior', studentInfo }: WelcomeScreenProps) {
  const isJunior = educationLevel === 'junior';
  const info = studentInfo || {};
  const sampleQuestions = generateQuestions(info, isJunior);

  const features = isJunior
    ? [
        {
          icon: <MessageSquare className="w-6 h-6 text-blue-600" />,
          title: "智能对话",
          description: "基于张雪峰视频知识库，提供专业的中考升学建议"
        },
        {
          icon: <Sparkles className="w-6 h-6 text-purple-600" />,
          title: "个性化推荐",
          description: "根据学生成绩和偏好，智能推荐合适的高中"
        },
        {
          icon: <GraduationCap className="w-6 h-6 text-green-600" />,
          title: "权威知识",
          description: "整合张雪峰老师的专业视频内容，来源可追溯"
        }
      ]
    : [
        {
          icon: <MessageSquare className="w-6 h-6 text-blue-600" />,
          title: "智能对话",
          description: "基于张雪峰视频知识库，提供专业的志愿填报建议"
        },
        {
          icon: <Sparkles className="w-6 h-6 text-purple-600" />,
          title: "个性化推荐",
          description: "根据学生成绩和偏好，智能推荐合适的院校和专业"
        },
        {
          icon: <GraduationCap className="w-6 h-6 text-green-600" />,
          title: "权威知识",
          description: "整合张雪峰老师的专业视频内容，来源可追溯"
        }
      ];

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* 欢迎标题 */}
        <div className="text-center space-y-3">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-lg ${
            isJunior 
              ? 'bg-gradient-to-br from-blue-500 to-cyan-600' 
              : 'bg-gradient-to-br from-blue-500 to-purple-600'
          }`}>
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            数字张雪峰
          </h1>
          <p className="text-lg text-gray-600">
            {isJunior ? 'AI中考升学咨询系统' : 'AI高考志愿填报咨询系统'}
          </p>
          <p className="text-sm text-gray-500">
            基于张雪峰老师的专业知识，为您提供智能、精准的{isJunior ? '升学' : '志愿填报'}建议
          </p>
        </div>

        {/* 功能特色 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((feature, idx) => (
            <Card key={idx} className="border-2 hover:border-blue-300 transition-colors">
              <CardContent className="p-6 text-center space-y-3">
                <div className="flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* 示例问题 */}
        <Card className={`border-2 border-dashed ${isJunior ? 'border-blue-200 bg-blue-50/30' : 'border-blue-200 bg-blue-50/30'}`}>
          <CardContent className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              开始探索
            </h2>
            <p className="text-sm text-gray-600">
              选择一个示例问题，或者直接输入您的问题开始对话
            </p>
            <div className="space-y-3">
              {sampleQuestions.map((item, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-4 px-4 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  onClick={() => onStartChat(item.question)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 text-blue-600">
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{item.question}</p>
                      <p className="text-xs text-gray-500 mt-1">{item.category}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 使用提示 */}
        <div className="text-center space-y-2">
          <p className="text-xs text-gray-500">
            💡 提示：完善学生信息后，AI将为您提供更精准的个性化推荐
          </p>
          <p className="text-xs text-gray-400">
            所有建议均基于张雪峰老师的视频内容，仅供参考
          </p>
        </div>
      </div>
    </div>
  );
}
